import linkupService from '../../services/linkupService'
import { MODEL_MODES } from '../../LLM/index.js'

/**
 * Search with Linkup service and enhance with LLM processing
 * @param {string} query - Search query
 * @param {string} searchType - Type of search ('web' or 'academic')
 * @param {Object} llmService - LLM service instance for answer enhancement
 * @returns {Promise<Object>} Search result with enhanced answer, sources, and images
 */
export const searchWithLinkup = async (query, searchType = 'web', llmService = null) => {
  try {
    console.log(`🔍 Starting ${searchType} search for:`, query)
    
    // Determine region based on search type
    const region = searchType === 'academic' ? 'International' : 'Indonesia'
    
    // Call linkup service with appropriate parameters
    const linkupResult = await linkupService.search(
      query,
      searchType, // 'web' or 'academic'
      'searchResults', // outputType
      region // region filter
    )
    
    console.log(`✅ ${searchType} search completed:`, {
      answerLength: linkupResult.answer?.length || 0,
      sourcesCount: linkupResult.sources?.length || 0,
      imagesCount: linkupResult.images?.length || 0
    })
    
    let enhancedAnswer = linkupResult.answer || 'Maaf, tidak dapat menemukan jawaban untuk pertanyaan Anda.'
    
    // Enhance answer with LLM if service is available and we have sources
    if (llmService && linkupResult.sources && linkupResult.sources.length > 0) {
      try {
        console.log('🤖 Enhancing answer with GENIUS AI model...')
        
        // Prepare sources context for LLM
        const sourcesContext = linkupResult.sources.map((source, index) => 
          `[${index + 1}] ${source.title}\n${source.snippet}\nSumber: ${source.domain}`
        ).join('\n\n')
        
        // Create enhanced prompt for LLM with detailed instructions for comprehensive answers
        const enhancementPrompt = `Berdasarkan pertanyaan pengguna dan sumber-sumber informasi berikut, berikan jawaban yang sangat komprehensif, detail, dan berkualitas tinggi dengan minimal 1000-1500 kata.

PERTANYAAN: ${query}

SUMBER INFORMASI:
${sourcesContext}

INSTRUKSI DETAIL:
1. STRUKTUR JAWABAN:
   - Mulai dengan pengantar yang menjelaskan konteks dan pentingnya topik
   - Bagi jawaban menjadi beberapa bagian utama dengan heading yang jelas
   - Setiap bagian harus memiliki minimal 2-3 paragraf yang detail
   - Akhiri dengan kesimpulan yang komprehensif dan implikasi praktis

2. KONTEN YANG HARUS DISERTAKAN:
   - Definisi dan konsep dasar yang relevan
   - Analisis mendalam dari berbagai aspek topik
   - Contoh konkret dan studi kasus jika relevan
   - Perbandingan dengan topik terkait atau alternatif
   - Perkembangan terkini dan tren masa depan
   - Dampak atau implikasi praktis
   - Perspektif dari berbagai sudut pandang

3. KUALITAS PENULISAN:
   - Gunakan bahasa yang jelas, formal, dan mudah dipahami
   - Setiap paragraf minimal 4-5 kalimat yang substantif
   - Gunakan transisi yang smooth antar paragraf
   - Berikan penjelasan yang detail dan tidak superficial
   - Hindari pengulangan informasi yang tidak perlu

4. PENGGUNAAN SUMBER:
   - Gunakan sitasi [1], [2], dst. untuk merujuk ke sumber yang relevan
   - Distribusikan sitasi secara merata di seluruh jawaban
   - Jika ada informasi yang bertentangan antar sumber, analisis dan jelaskan perbedaannya
   - Integrasikan informasi dari berbagai sumber untuk memberikan perspektif yang holistik

5. TARGET PANJANG:
   - Minimal 1000 kata, target ideal 1200-1500 kata
   - Pastikan setiap kata memberikan nilai tambah dan informasi yang berguna
   - Jangan menambahkan filler text, fokus pada konten yang berkualitas

6. FORMAT MARKDOWN:
   - Gunakan heading (##) untuk bagian utama
   - Gunakan subheading (###) untuk sub-bagian jika diperlukan
   - Gunakan bullet points atau numbered lists untuk informasi yang terstruktur
   - Gunakan **bold** untuk penekanan pada poin-poin penting
   - Gunakan *italic* untuk istilah teknis atau asing

JAWABAN KOMPREHENSIF:`
        
        const llmResponse = await llmService.sendMessage(
          [{ role: 'user', content: enhancementPrompt }],
          { 
            mode: MODEL_MODES.GENIUS,
            maxTokens: searchType === 'academic' ? 6000 : 5000,
            searchType: searchType,
            stream: false,
            consumeCredits: false // Don't consume credits for answer enhancement
          }
        )
        
        if (llmResponse && llmResponse.trim().length > 0) {
          enhancedAnswer = llmResponse.trim()
          console.log('✅ Answer enhanced with GENIUS AI model')
        }
        
      } catch (llmError) {
        console.warn('⚠️ Failed to enhance answer with LLM, using original:', llmError)
        // Keep original answer if LLM enhancement fails
      }
    }
    
    // Validate and format the result
    const formattedResult = {
      answer: enhancedAnswer,
      sources: linkupResult.sources || [],
      images: linkupResult.images || [],
      searchType: searchType,
      region: region,
      rawSourcesCount: linkupResult.rawSourcesCount || 0,
      validSourcesCount: linkupResult.validSourcesCount || 0,
      hasFallbackSources: linkupResult.hasFallbackSources || false,
      enhancedWithLLM: llmService && linkupResult.sources && linkupResult.sources.length > 0
    }
    
    return formattedResult
    
  } catch (error) {
    console.error(`❌ Error in ${searchType} search:`, error)
    
    // Return error result with fallback message
    return {
      answer: `Maaf, terjadi kesalahan saat melakukan pencarian ${searchType}. Silakan coba lagi nanti.`,
      sources: [],
      images: [],
      searchType: searchType,
      region: searchType === 'academic' ? 'International' : 'Indonesia',
      error: error.message || 'Unknown error',
      rawSourcesCount: 0,
      validSourcesCount: 0,
      hasFallbackSources: false,
      enhancedWithLLM: false
    }
  }
}

/**
 * Get factual information using web search (Indonesia sources)
 * @param {string} query - Search query
 * @param {Object} llmService - LLM service instance for answer enhancement
 * @returns {Promise<Object>} Search result
 */
export const getFactualInfo = async (query, llmService = null) => {
  return await searchWithLinkup(query, 'web', llmService)
}

/**
 * Get academic/scientific information using academic search (International sources)
 * @param {string} query - Search query
 * @param {Object} llmService - LLM service instance for answer enhancement
 * @returns {Promise<Object>} Search result
 */
export const getAcademicInfo = async (query, llmService = null) => {
  return await searchWithLinkup(query, 'academic', llmService)
}

/**
 * Extract key facts from search answer
 * @param {string} answer - Answer text
 * @returns {Array} Array of key facts
 */
export const extractKeyFacts = (answer) => {
  if (!answer || typeof answer !== 'string') {
    return []
  }
  
  // Split by sentences and filter meaningful ones
  const sentences = answer.split(/[.!?]+/).filter(s => s.trim().length > 20)
  return sentences.map(s => s.trim()).filter(s => s.length > 0)
}

/**
 * Format sources for display
 * @param {Array} sources - Sources array
 * @returns {Array} Formatted sources
 */
export const formatSources = (sources) => {
  if (!sources || !Array.isArray(sources)) {
    return []
  }
  
  return sources.map((source, index) => ({
    id: source.id || index + 1,
    title: source.title || 'Untitled Source',
    snippet: source.snippet || 'No description available',
    url: source.url || '#',
    domain: source.domain || 'Unknown',
    isFallback: source.isFallback || false
  }))
}

/**
 * Format images for display
 * @param {Array} images - Images array
 * @returns {Array} Formatted images
 */
export const formatImages = (images) => {
  if (!images || !Array.isArray(images)) {
    return []
  }
  
  return images.map((image, index) => ({
    id: image.id || index + 1,
    url: image.url || '',
    title: image.title || `Image ${index + 1}`,
    description: image.description || '',
    source: image.source || 'Unknown'
  })).filter(img => img.url) // Only include images with valid URLs
}

/**
 * Parse citations from answer text
 * @param {string} answer - Answer text with citations
 * @returns {Array} Array of citation numbers found in the text
 */
export const parseCitations = (answer) => {
  if (!answer || typeof answer !== 'string') {
    return []
  }
  
  const citationRegex = /\[(\d+)\]/g
  const citations = []
  let match
  
  while ((match = citationRegex.exec(answer)) !== null) {
    const citationNumber = parseInt(match[1])
    if (!citations.includes(citationNumber)) {
      citations.push(citationNumber)
    }
  }
  
  return citations.sort((a, b) => a - b)
}

/**
 * Validate search result
 * @param {Object} result - Search result object
 * @returns {boolean} Whether the result is valid
 */
export const validateSearchResult = (result) => {
  if (!result || typeof result !== 'object') {
    return false
  }
  
  // Check required fields
  const hasAnswer = typeof result.answer === 'string' && result.answer.length > 0
  const hasSources = Array.isArray(result.sources)
  const hasImages = Array.isArray(result.images)
  
  return hasAnswer && hasSources && hasImages
}

/**
 * Get search statistics
 * @param {Object} result - Search result object
 * @returns {Object} Statistics about the search result
 */
export const getSearchStats = (result) => {
  if (!validateSearchResult(result)) {
    return {
      answerWordCount: 0,
      sourcesCount: 0,
      imagesCount: 0,
      citationsCount: 0,
      hasValidSources: false
    }
  }
  
  const answerWordCount = result.answer.split(/\s+/).filter(word => word.length > 0).length
  const sourcesCount = result.sources.length
  const imagesCount = result.images.length
  const citationsCount = parseCitations(result.answer).length
  const hasValidSources = result.sources.some(source => !source.isFallback)
  
  return {
    answerWordCount,
    sourcesCount,
    imagesCount,
    citationsCount,
    hasValidSources
  }
}